import React from 'react';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus, Calendar, Utensils } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';
import { mockSubscriptions } from './mock-data';

const columns = [
  {
    accessorKey: 'id',
    header: 'Subscription ID',
    cell: ({ row }) => (
      <div className="font-medium">{row.original.id}</div>
    )
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => (
      <div>
        <div className="font-medium">{row.original.customer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.customer.email}</div>
      </div>
    )
  },
  {
    accessorKey: 'plan',
    header: 'Plan',
    cell: ({ row }) => (
      <div>
        <Badge
          variant={row.original.plan.name.includes('HUVUDROLL') ? 'default' : 'outline'}
          className={row.original.plan.name.includes('HUVUDROLL') ? 'bg-[#FFDB00] border-[#FFDB00] text-[#0058AB]' : ''}
        >
          {row.original.plan.name}
        </Badge>
        <div className="text-sm text-muted-foreground mt-1">
          ${row.original.plan.price}/month
        </div>
      </div>
    )
  },
  {
    accessorKey: 'containers',
    header: 'Containers',
    cell: ({ row }) => (
      <div className="space-y-1">
        {row.original.containers.map((container) => (
          <div key={container.id} className="flex items-center text-sm">
            {container.name.includes('HUVUDROLL') && (
              <Utensils className="w-4 h-4 mr-1 text-[#0058AB]" />
            )}
            {container.name}
            <Badge variant="outline" className="ml-2">
              {container.refillSchedule}
            </Badge>
          </div>
        ))}
      </div>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'}>
        {row.original.status}
      </Badge>
    )
  },
  {
    accessorKey: 'nextBilling',
    header: 'Next Billing',
    cell: ({ row }) => (
      <div className="flex items-center">
        <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
        {new Date(row.original.nextBilling).toLocaleDateString()}
      </div>
    )
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>View Details</DropdownMenuItem>
          <DropdownMenuItem>Manage Containers</DropdownMenuItem>
          <DropdownMenuItem>Update Plan</DropdownMenuItem>
          <DropdownMenuItem>Cancel Subscription</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
];

export function RefillSubscriptionsPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    plan: 'all'
  });

  const subscriptions = React.useMemo(() => {
    let filteredSubscriptions = [...mockSubscriptions];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredSubscriptions = filteredSubscriptions.filter(sub =>
        sub.customer.name.toLowerCase().includes(searchLower) ||
        sub.customer.email.toLowerCase().includes(searchLower) ||
        sub.id.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      filteredSubscriptions = filteredSubscriptions.filter(sub =>
        sub.status === filters.status
      );
    }

    if (filters.plan !== 'all') {
      filteredSubscriptions = filteredSubscriptions.filter(sub =>
        sub.plan.name === filters.plan
      );
    }

    return filteredSubscriptions;
  }, [filters]);

  const plans = React.useMemo(() =>
    Array.from(new Set(mockSubscriptions.map(sub => sub.plan.name))),
    []
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Refill Subscriptions</h1>
          <p className="text-sm text-muted-foreground">
            Manage customer subscriptions and recurring refills
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Subscription
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search subscriptions..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.plan}
                onValueChange={(value) => setFilters(prev => ({ ...prev, plan: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  {plans.map(plan => (
                    <SelectItem key={plan} value={plan}>{plan}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={subscriptions}
            emptyMessage="No subscriptions found"
            loadingMessage="Loading subscriptions..."
          />
        </CardContent>
      </Card>

      {/* Special Meatball Subscription Highlight */}
      <Card className="bg-[#FFDB00] border-[#0058AB] border-2">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Utensils className="w-12 h-12 text-[#0058AB]" />
              <div>
                <h2 className="text-xl font-bold text-[#0058AB]">HUVUDROLL Weekly Subscription</h2>
                <p className="text-[#0058AB]">
                  Our premium meatball subscription with exclusive benefits!
                </p>
              </div>
            </div>
            <Button className="bg-[#0058AB] hover:bg-[#004F9B]">
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 
